import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";

// 📌 Fetch all blogs with search & pagination
export const fetchBlogs = createAsyncThunk(
    "blogs/fetchAll",
    async ({ search = "", page = 1, limit = 10 } = {}, { rejectWithValue }) => {
        try {
            const { data } = await api.get(`/blogs?search=${search}&page=${page}&limit=${limit}`);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Something went wrong" });
        }
    }
);

// 📌 Fetch single blog by permalink
export const fetchBlogByPermalink = createAsyncThunk(
    "blogs/fetchByPermalink",
    async (permalink, { rejectWithValue }) => {
        try {
            const { data } = await api.get(`/blog/permalink/${permalink}`);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Something went wrong" });
        }
    }
);

// 📌 Fetch single blog by blogId
export const fetchBlogById = createAsyncThunk(
    "blogs/fetchById",
    async (blogId, { rejectWithValue }) => {
        try {
            const { data } = await api.get(`/single-blog/${blogId}`);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Something went wrong" });
        }
    }
);

// 📌 Add new blog (Admin)
export const addBlog = createAsyncThunk(
    "blogs/add",
    async (formData, { rejectWithValue }) => {
        try {
            const { data } = await api.post("/create-blog", formData, {
                withCredentials: true,
                headers: { "Content-Type": "multipart/form-data" },
            });
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Something went wrong" });
        }
    }
);

// 📌 Update blog (Admin)
export const updateBlog = createAsyncThunk(
    "blogs/update",
    async ({ id, formData }, { rejectWithValue }) => {
        console.log()
        try {
            const { data } = await api.put(`/update-blog/${id}`, formData, {
                withCredentials: true,
                headers: { "Content-Type": "multipart/form-data" },
            });
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Something went wrong" });
        }
    }
);

// 📌 Delete blog (Admin)
export const deleteBlog = createAsyncThunk(
    "blogs/delete",
    async (id, { rejectWithValue }) => {
        try {
            const { data } = await api.delete(`/delete-blog/${id}`, { withCredentials: true });
            return { ...data, id };
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Something went wrong" });
        }
    }
);

// Initial State
const initialState = {
    blogs: [],
    singleBlog: null,
    pagination: {
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 0,
    },
    loading: false,
    error: null,
    success: null,
};

const blogSlice = createSlice({
    name: "blogs",
    initialState,
    reducers: {
        clearBlogMessages: (state) => {
            state.error = null;
            state.success = null;
        },
        clearSingleBlog: (state) => {
            state.singleBlog = null;
        },
        setBlogPagination: (state, action) => {
            state.pagination = { ...state.pagination, ...action.payload };
        },
    },
    extraReducers: (builder) => {
        builder
            // ✅ Fetch all blogs
            .addCase(fetchBlogs.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchBlogs.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload.success) {
                    // ✅ Correct extraction based on API structure
                    state.blogs = action.payload.data || [];
                    state.pagination = action.payload.pagination || {
                        total: 0,
                        page: 1,
                        limit: 10,
                        totalPages: 0,
                    };
                } else {
                    state.error = action.payload.message;
                }
            })
            .addCase(fetchBlogs.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message;
            })

            // ✅ Fetch blog by permalink
            .addCase(fetchBlogByPermalink.fulfilled, (state, action) => {
                if (action.payload.success) {
                    state.singleBlog = action.payload.data;
                } else {
                    state.error = action.payload.message;
                }
            })
            .addCase(fetchBlogByPermalink.rejected, (state, action) => {
                state.error = action.payload?.message;
            })

            // ✅ Fetch blog by blogId
            .addCase(fetchBlogById.fulfilled, (state, action) => {
                if (action.payload.success) {
                    state.singleBlog = action.payload.data;
                } else {
                    state.error = action.payload.message;
                }
            })
            .addCase(fetchBlogById.rejected, (state, action) => {
                state.error = action.payload?.message;
            })

            // ✅ Add blog
            .addCase(addBlog.fulfilled, (state, action) => {
                if (action.payload.success) {
                    state.blogs.unshift(action.payload.data);
                    state.success = action.payload.message;
                } else {
                    state.error = action.payload.message;
                }
            })
            .addCase(addBlog.rejected, (state, action) => {
                state.error = action.payload?.message;
            })

            // ✅ Update blog
            .addCase(updateBlog.fulfilled, (state, action) => {
                if (action.payload.success) {
                    const index = state.blogs.findIndex(
                        (blog) => blog._id === action.payload.data._id
                    );
                    if (index !== -1) {
                        state.blogs[index] = action.payload.data;
                    }
                    state.success = action.payload.message;
                } else {
                    state.error = action.payload.message;
                }
            })
            .addCase(updateBlog.rejected, (state, action) => {
                state.error = action.payload?.message;
            })

            // ✅ Delete blog
            .addCase(deleteBlog.fulfilled, (state, action) => {
                if (action.payload.success) {
                    state.blogs = state.blogs.filter((blog) => blog._id !== action.payload.id);
                    state.success = action.payload.message;
                } else {
                    state.error = action.payload.message;
                }
            })
            .addCase(deleteBlog.rejected, (state, action) => {
                state.error = action.payload?.message;
            });
    },
});

export const { clearBlogMessages, clearSingleBlog, setBlogPagination } =
    blogSlice.actions;
export default blogSlice.reducer;
